// src/components/studentPortal/DashboardHome.jsx
// Section 1 of the redesigned Student Portal (StudentPortal.jsx). Reuses the
// existing InstituteBanner (extracted from Dashboard.jsx, not rebuilt).
// "Latest Updates" and "Featured for You" are UI shells with static content -
// there is no backend for either yet, clearly marked below rather than
// pretending they're wired to something real.
import React, { useState, useEffect } from 'react';
import { FiFilm, FiArrowRight, FiUsers, FiBell, FiStar, FiFolder } from 'react-icons/fi';
import InstituteBanner from '../InstituteBanner';
import { baseUrl } from '../../config';

const PROJECT_TYPE_LABEL = { short: 'Short Film', full: 'Feature Film', advertisement: 'Advertisement' };

const ProjectCard = ({ project }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-300 hover:shadow-md transition-all duration-200">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white mb-3">
      <FiFilm size={18} />
    </div>
    <h4 className="font-semibold text-slate-800 truncate">{project.title || 'Untitled project'}</h4>
    <p className="text-xs text-slate-500 mt-0.5">{PROJECT_TYPE_LABEL[project.project_type] || project.project_type || 'Project'}</p>
    <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, project.progress || 0))}%` }}
      />
    </div>
    <p className="text-[11px] text-slate-400 mt-1">{project.progress || 0}% complete</p>
  </div>
);

const Panel = ({ title, icon: Icon, action, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
        <Icon size={15} className="text-teal-600" /> {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

// Skeleton mimics the real ProjectCard's shape (icon + title-line + progress
// bar) instead of a single flat block, so loading reads as "content is
// coming" rather than an unstyled placeholder.
const ProjectCardSkeleton = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 animate-pulse">
    <div className="w-10 h-10 rounded-xl bg-slate-200 mb-3" />
    <div className="h-3.5 w-3/4 rounded bg-slate-200 mb-2" />
    <div className="h-2.5 w-1/2 rounded bg-slate-100 mb-4" />
    <div className="h-1.5 rounded-full bg-slate-100" />
  </div>
);

const DashboardHome = ({ onNavigate }) => {
  const [projects, setProjects] = useState([]);
  const [projectsUsed, setProjectsUsed] = useState(null);
  const [projectsLimit, setProjectsLimit] = useState(8);
  const [institutionName, setInstitutionName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const userRes = await fetch(`${baseUrl}/getUserById`, { headers: { Authorization: `Bearer ${token}` } });
        if (!userRes.ok) return;
        const user = await userRes.json();

        const [projectsRes, statusRes] = await Promise.all([
          fetch(`${baseUrl}/getProjectsByUserId`, { headers: { Authorization: `Bearer ${token}`, user_id: String(user.user_id) } }),
          fetch(`${baseUrl}/free-tier/status`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (statusRes.ok) {
          const status = await statusRes.json();
          setProjectsUsed(status.projects_used);
          setProjectsLimit(status.projects_limit || 8);
        }

        if (user.institution_id) {
          const instRes = await fetch(`${baseUrl}/student-program/my-institution`, { headers: { Authorization: `Bearer ${token}` } });
          if (instRes.ok) setInstitutionName((await instRes.json()).name);
        }
      } catch (_) {
        // Fail quiet - an empty/partial home screen is fine, never blocks the page.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <InstituteBanner />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-800">Your Projects</h2>
        <button
          onClick={() => onNavigate('projects')}
          className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1"
        >
          {projectsUsed != null ? `${projectsUsed} of ${projectsLimit} used` : ''} <FiArrowRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <ProjectCardSkeleton key={i} />)
        ) : projects.length === 0 ? (
          <button
            onClick={() => onNavigate('projects')}
            className="col-span-full rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 flex flex-col items-center gap-2 text-slate-500 hover:border-teal-400 hover:text-teal-600 transition-colors duration-200"
          >
            <FiFolder size={22} className="text-slate-300" />
            <span>No projects yet - create your first one in My Projects.</span>
          </button>
        ) : (
          projects.slice(0, 3).map((p) => <ProjectCard key={p.project_id} project={p} />)
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Latest Updates" icon={FiBell}>
          {/* UI shell - no backend for student updates/announcements yet. */}
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="pb-3 border-b border-slate-100">Welcome to your new Student Portal - explore My Projects to get started.</li>
            <li>Tasks and Certificates is coming soon.</li>
          </ul>
        </Panel>

        <Panel title="Featured for You" icon={FiStar}>
          {/* UI shell - no backend for personalized recommendations yet. */}
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="pb-3 border-b border-slate-100">Try the Script Analysis tool on your next project.</li>
            <li>See what other students are building in the Community.</li>
          </ul>
        </Panel>

        <Panel
          title="Your Institute"
          icon={FiUsers}
          action={
            <button onClick={() => onNavigate('alumni')} className="text-xs font-medium text-teal-600 hover:text-teal-700">
              Alumni →
            </button>
          }
        >
          <p className="text-sm text-slate-600">
            {institutionName ? (
              <>You're part of <span className="font-semibold text-slate-800">{institutionName}</span>'s student network.</>
            ) : (
              'Connect with your institute community.'
            )}
          </p>
        </Panel>
      </div>
    </div>
  );
};

export default DashboardHome;
